import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatUtcDate } from '@/ui/format';

import { api } from '../client';
import { queryKeys } from './keys';
import type { McpConsent, McpConsentAnswer, McpConsentRedirect, McpToken, McpTokenCreated, McpTokenExtendInput, McpTokenInput } from '../types';

/** The caller's tokens, newest first: never the token itself. */
export function useMcpTokens({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: queryKeys.mcpTokens,
    queryFn: () => api<{ tokens: McpToken[] }>('/api/mcp/tokens'),
  });
}

/**
 * The answer carries the token once. `gcTime: 0` drops the mutation (and with it the secret) from the
 * client as soon as nothing observes it; the dialog holds the secret in its own state and nowhere else.
 * Quiet: the dialog shows the outcome, and a success toast would sit beside the secret.
 */
export function useCreateMcpToken() {
  const client = useQueryClient();
  return useMutation({
    gcTime: 0,
    meta: { quiet: true },
    mutationFn: (body: McpTokenInput) => api<McpTokenCreated>('/api/mcp/tokens', 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.mcpTokens }),
  });
}

export function useRevokeMcpToken() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: 'Token revoked' },
    mutationFn: (id: string) => api<void>(`/api/mcp/tokens/${id}`, 'DELETE'),
    // Settled: a 404 means it was already revoked (another tab), so the list is stale either way.
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.mcpTokens }),
  });
}

/** The new expiry is the end of `expires_on` in UTC; the server refuses one that is not later than the current. */
export function useExtendMcpToken() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (token: McpToken) => `Token extended to ${formatUtcDate(token.expires_at)} UTC` },
    mutationFn: ({ id, ...body }: McpTokenExtendInput & { id: string }) => api<McpToken>(`/api/mcp/tokens/${id}/extend`, 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.mcpTokens }),
  });
}

/**
 * What an AI tool asks to be allowed. The sealed request expires after 10 minutes and a refusal
 * (expired, no access) does not get better on retry, so it is asked once and never refetched.
 */
export function useMcpConsent(request: string | null) {
  return useQuery({
    enabled: Boolean(request),
    queryKey: queryKeys.mcpConsent(request ?? ''),
    queryFn: () => api<McpConsent>(`/api/mcp/oauth/consent?request=${encodeURIComponent(request ?? '')}`),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Allow or deny. Quiet: the page shows a refusal in place of its buttons, and on success the browser leaves. */
export function useAnswerMcpConsent() {
  return useMutation({
    meta: { quiet: true },
    mutationFn: (body: McpConsentAnswer) => api<McpConsentRedirect>('/api/mcp/oauth/consent', 'POST', body),
  });
}
